/**
 * A short audio file, fetched with the session cookie and played by an element this app
 * never renders.
 *
 * EXTRACTED FROM THE OPENING LINE'S PLAYER, unchanged in behaviour, because the voice
 * picker needs the same three hundred milliseconds of care and none of the scrubber. What
 * the two share is not the look - one is a transport with a clock, the other is a 32px
 * play/stop square in a list of forty rows - it is everything underneath: the fetch, the
 * object URL, the revoke on unmount, and the fact that a failure has to become a sentence
 * rather than a button that silently does nothing.
 *
 * FETCHED RATHER THAN POINTED AT WITH `src`. Both routes need the cookie and both answer
 * 404 for something that is not this agent's, and a bare `src` gives neither
 * `credentials: "include"` on a split-origin dev setup nor any way to show what went wrong.
 *
 * THERE IS NO `<audio>` ELEMENT IN THE TREE. The element is a plain `new Audio()` in a ref:
 * it is the playback engine and never a control. The browser's own player is a slab of
 * somebody else's design language, and it only reaches its full size once it has bytes - so
 * the row it sits in changes shape when you press play.
 *
 * `stop()` IS THE ONE THING THE PLAYER DID NOT NEED. A settings page has one recording on
 * it; the picker has forty, and two voices talking over each other is the fastest way to
 * make a list of voices useless. The caller decides which clip is the live one and stops
 * the rest.
 */

import { useEffect, useRef, useState } from "react";
import { blob } from "../api";

export interface Clip {
  playing: boolean;
  /** The first press fetches. Everything after it is instant. */
  loading: boolean;
  error: string | null;
  /** Seconds. */
  at: number;
  total: number;
  toggle: () => void;
  /** A fraction of the whole, from a scrubber. Loads the file and leaves it paused. */
  seek: (fraction: number) => void;
  stop: () => void;
}

export function useClip(path: string, durationMs = 0): Clip {
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [at, setAt] = useState(0);
  // Known before a byte is fetched where the caller has it, so the clock and the track are
  // honest on first paint rather than counting up from an unknown total.
  const [total, setTotal] = useState(durationMs / 1000);
  const [error, setError] = useState<string | null>(null);

  const element = useRef<HTMLAudioElement | null>(null);
  const objectUrl = useRef<string | null>(null);

  useEffect(
    () => () => {
      element.current?.pause();
      if (objectUrl.current) URL.revokeObjectURL(objectUrl.current);
    },
    [],
  );

  /** The bytes, once, on the first thing that needs them. */
  async function ready(): Promise<HTMLAudioElement | null> {
    if (element.current) return element.current;
    setLoading(true);
    setError(null);
    try {
      const bytes = await blob(path);
      objectUrl.current = URL.createObjectURL(bytes);
      const audio = new Audio(objectUrl.current);
      audio.addEventListener("timeupdate", () => setAt(audio.currentTime));
      audio.addEventListener("play", () => setPlaying(true));
      audio.addEventListener("pause", () => setPlaying(false));
      // Back to the start rather than parked at the end, so the next press plays rather
      // than doing nothing.
      audio.addEventListener("ended", () => {
        audio.currentTime = 0;
        setAt(0);
        setPlaying(false);
      });
      // The file's own duration wins over the declared one if they ever disagree.
      audio.addEventListener("durationchange", () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) setTotal(audio.duration);
      });
      element.current = audio;
      return audio;
    } catch {
      setError("Could not load the recording");
      return null;
    } finally {
      setLoading(false);
    }
  }

  async function toggle() {
    const audio = await ready();
    if (!audio) return;
    if (audio.paused) {
      try {
        await audio.play();
      } catch {
        setError("Could not play the recording");
      }
    } else {
      audio.pause();
    }
  }

  async function seek(fraction: number) {
    const seconds = Math.min(total, Math.max(0, fraction * total));
    setAt(seconds);
    const audio = await ready();
    if (audio) audio.currentTime = seconds;
  }

  /**
   * Silence, and back to the start.
   *
   * NOT A PAUSE. This is what one row does when another row is pressed, and leaving it
   * parked half way through would mean the next press on it resumes from the middle of a
   * word - which reads as a bug in a list somebody is comparing voices in.
   */
  function stop() {
    const audio = element.current;
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
    setAt(0);
  }

  return { playing, loading, error, at, total, toggle: () => void toggle(), seek: (f) => void seek(f), stop };
}
